import React, { useEffect, useState } from 'react'
import {
  Button, Col, DatePicker, Divider, Drawer, Form, Input, InputNumber, Popconfirm, Row, Select, Space,
  Switch, Tag, Tooltip, Typography
} from 'antd'
import {
  AlertOutlined, CheckOutlined, DeleteOutlined, PlusOutlined, SaveOutlined, SplitCellsOutlined
} from '@ant-design/icons'
import moment from 'moment'
import { getCookie } from 'react-use-cookie'

import { urlKey, urlType } from '../../../services/api/api-urls'
import { ApiRequest } from '../../../services/api/api'
import { commonFormSelect, getSelectOptions } from '../../../components/common/common-form/common-form-select'
import { validateMessages } from '../../../components/common/common-form/common-form'
import SweetAlertService from '../../../services/lib/utils/sweet-alert-service'
import { addWorkdaysWithHolidays } from '../../../services/others/holiday-services'
import type { ApprovalOrder } from './order-approved-page'
import OrderApprovedSplitModal, { SplitOptions } from './order-approved-split-modal'

// 编辑逻辑（字段联动、报价取价、库存检查、保存接口）与 Order Edit 弹窗
// sales-order-product-management-dialog 保持一致，只是界面换成抽屉布局。
// 原弹窗被多个页面共用，所以复制逻辑而不是改它。

const { Text } = Typography

interface IOrderProduct {
  orderProductId?: any
  orderId?: any
  productId?: any
  quantity?: any
  unitPrice?: any
  price?: any
  marginOfError?: any
  packagingQty?: any
}

interface IOrderOption {
  optionId?: any
  quantity?: any
  unitPrice?: any
  price?: any
}

const getLeadtimeFlag = (orderData: any): boolean => {
  if (orderData.leadtimeFlag == 1) return true
  if (orderData.leadtimeFlag == 0) return false
  if (!('leadtimeFlag' in orderData)) return true // new order
  if (orderData.leadtimeFlag == null) return false // old data
  return true
}

// 后端返回 UTC 时间（不带 Z），转成本地时间
const utcToLocal = (date: any) => (date ? moment.utc(date).local() : undefined)

const fieldLabel = (label: string, required?: boolean) => (
  <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
    {required && <span style={{ color: '#ff4d4f' }}>* </span>}{label}
  </div>
)

const OrderApprovedEditDrawer = (props: {
  order: ApprovalOrder | null
  visible: boolean
  onClose: () => void
  onSaved: () => void
  onApprove: (order: ApprovalOrder) => void
  onSplit: (order: ApprovalOrder, options: SplitOptions) => void
}) => {
  const { order, visible, onClose, onSaved, onApprove, onSplit } = props
  // 接口返回的原始订单数据
  const orderData: any = order?.raw

  const [form] = Form.useForm()
  const [, setOrderProductChangedValueIndex] = useState<any>()
  const [orderOptionChangedValueIndex, setOrderOptionChangedValueIndex] = useState<any>()
  const [subtotal, setSubtotal] = useState<any>(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [splitVisible, setSplitVisible] = useState(false)

  // store selection options from apis request
  const [customerOptions, setCustomerOptions] = useState<any[]>([])
  const [employeeOptions, setEmployeeOptions] = useState<any[]>([])
  const [deliveryMethodOptions, setDeliveryMethodOptions] = useState<any[]>([])
  const [orderStatusOptions, setOrderStatusOptions] = useState<any[]>([])
  const [deliveryCityOptions, setDeliveryCityOptions] = useState<any[]>([])
  const [productsOptions, setProductsOptions] = useState<any[]>([])
  const [productOptionOptions, setProductOptionOptions] = useState([])
  const [addressOptions, setAddressOptions] = useState([])
  const [paymentCycleOptions, setPaymentCycleOptions] = useState([])
  const [leadtimeFlag, setLeadtimeFlag] = useState<boolean>(true)
  const [group1Id, setGroup1Id] = useState<any>(null)
  const [, setPriceWarnings] = useState<{ [key: number]: boolean }>({})

  // 当前选中的配送地址：选过下拉后是 addressId，原订单数据里是地址文本
  const deliveryAddressValue = Form.useWatch('deliveryAddress', form)
  const selectedAddress: any = addressOptions.find((row: any) =>
    typeof deliveryAddressValue === 'number' ? row.addressId === deliveryAddressValue : row.address === deliveryAddressValue
  )

  useEffect(() => {
    if (!orderData) return
    setIsDirty(false)
    setLeadtimeFlag(getLeadtimeFlag(orderData))
    setPriceWarnings({})
    form.resetFields()
    form.setFieldsValue({
      ...orderData,
      orderStatusId: orderData.orderStatus?.orderStatusId ?? 1,
      leadtimeFlag: getLeadtimeFlag(orderData),
      paymentCycleId: orderData.customer?.paymentCycleId,
      orderProduct: (orderData.orderProduct || []).map((e: any) => ({
        ...e,
        packagingQty: e.product?.baseProduct?.packagingType?.quantity
      })),
      // Convert string values to boolean for Switch components
      warehouseNoteType: orderData.warehouseNoteType === 'Special',
      productionNoteType: orderData.productionNoteType === 'Special',
      accountNoteType: orderData.accountNoteType === 'Special',
      requiredDate: utcToLocal(orderData.requiredDate),
      deliveryDate: utcToLocal(orderData.deliveryDate),
      orderDate: utcToLocal(orderData.orderDate) || moment()
    })
    // 此时抽屉内容可能还没挂载、字段未注册，不能用 getFieldsValue() 回写，否则会把产品清空。
    // 价格直接用数据库里的值
    setSubtotal(getSubtotal(form))

    if (orderData.customerId) {
      setProducts(orderData.customerId)
      setAddress(orderData.customerId)
    }
    getSelectOptions('', 'Customer/GetAllSimpleCustomer').then(res => setCustomerOptions(res))
    getSelectOptions(urlKey.Employee).then(res => setEmployeeOptions(res))
    getSelectOptions(urlKey.DeliveryMethod).then(res => setDeliveryMethodOptions(res))
    getSelectOptions(urlKey.OrderStatus).then(res => setOrderStatusOptions(res))
    getSelectOptions(urlKey.City).then(res => setDeliveryCityOptions(res))
    getSelectOptions(urlKey.PaymentCycle).then(res => setPaymentCycleOptions(res))
    getSelectOptions(urlKey.ProductOption).then(res => setProductOptionOptions(res))
    fetchDeliveryFee()
  }, [orderData])

  const setProducts = (id: any) => {
    getSelectOptions('', 'Product/GetProductByCustomerIdWithQuota?id=' + id)
      .then(res => {
        setProductsOptions(res.map((row: any) => ({ ...row, packagingTypeQty: row.baseProduct?.packagingType?.quantity })))
      })
  }

  // 当产品选项加载完成后，检查并补充历史订单中的失效产品
  useEffect(() => {
    const fetchInactiveProducts = async () => {
      if (orderData?.orderProduct && productsOptions.length > 0) {
        const inactiveProducts: any[] = []
        const activeProductIds = new Set(productsOptions.map((p: any) => p.productId))

        // 查找订单中不在当前产品列表中的产品
        for (const orderProduct of orderData.orderProduct) {
          if (orderProduct.product && !activeProductIds.has(orderProduct.productId)) {
            try {
              // 使用包含失效产品的API获取产品信息
              const result = await ApiRequest({
                url: `Product/GetProductByIdIncludeInactive?id=${orderProduct.productId}`,
                method: 'get',
                isShowSpinner: false
              })
              if (result?.data?.data) {
                inactiveProducts.push({
                  ...result.data.data,
                  packagingTypeQty: result.data.data.baseProduct?.packagingType?.quantity,
                  isInactive: true // 标记为失效产品
                })
              }
            } catch (error) {
              console.warn(`Failed to fetch inactive product ${orderProduct.productId}:`, error)
            }
          }
        }

        // 将失效产品合并到选项列表中
        if (inactiveProducts.length > 0) {
          setProductsOptions(prev => [...prev, ...inactiveProducts])
        }
      }
    }

    fetchInactiveProducts()
  }, [orderData, productsOptions.length])

  const fetchDeliveryFee = async () => {
    try {
      if (orderData?.customer?.deliveryFeeFlag) {
        const resDeliveryFeeFlags = await ApiRequest({
          url: 'DeliveryMethod/GetAllDeliveryFeeFlags',
          method: 'get'
        })
        if (resDeliveryFeeFlags.data && resDeliveryFeeFlags.data.data) {
          const DeliveryFeeFlags = resDeliveryFeeFlags.data.data.find((e: any) => e.flagId === orderData.customer.deliveryFeeFlag)
          form.setFieldsValue({ deliveryFee: DeliveryFeeFlags?.flagName })
        }
      }
    } catch (error) {
      console.error('Error fetching customer info:', error)
    }
  }

  const setAddress = (id: any) => {
    getSelectOptions('', 'ExtraAddress/GetAllExtraAddress?customerId=' + id)
      .then(res => setAddressOptions(res))
  }

  const onFormChange = async (changedValues: any, newValues: any) => {
    setIsDirty(true)

    const changedValuesKey = Object.keys(changedValues)[0]
    let orderProductChangedValueIndex = 0
    let orderOptionChangedValueIndex = 0
    let orderProductChangedValue: IOrderProduct
    let orderOptionChangedValue: IOrderOption
    switch (changedValuesKey) {
      case 'customerId':
        if (changedValues['customerId']) {
          setCustomerInfosToForm(changedValues['customerId'], newValues)
          return
        }
        break
      case 'leadtimeFlag':
        setLeadtimeFlag(changedValues['leadtimeFlag'])
        if (changedValues['leadtimeFlag'] === true && newValues.leadtime) {
          // Switch to leadtime mode - calculate req date based on leadtime
          const baseDate = await addWorkdaysWithHolidays(getLeadtimeBaseDate(), newValues.leadtime)
          form.setFieldsValue({ ...newValues, requiredDate: moment(baseDate) })
          return
        }
        break
      case 'leadtime':
        if (changedValues['leadtime']) {
          // Use holiday-aware calculation
          const baseDate = await addWorkdaysWithHolidays(getLeadtimeBaseDate(), changedValues['leadtime'])
          form.setFieldsValue({ ...newValues, requiredDate: moment(baseDate) })
          return
        }
        break
      case 'deliveryAsap':
        if (changedValues['deliveryAsap']) {
          if (newValues.deliveryAsap == true) {
            const result = await SweetAlertService.inputConfirm({ type: 'text', title: 'Input reason for urgent', placeholder: 'qty', defaultValue: '', handleRequired: true })
            if (result == undefined) {
              form.setFieldsValue({ ...newValues, deliveryAsap: false, urgentNote: null })
            } else {
              form.setFieldsValue({ ...newValues, urgentNote: result })
            }
          }
          return
        }
        break
      case 'deliveryAddress':
        if (changedValues['deliveryAddress']) {
          const addr: any = addressOptions.filter((row: any) => row.addressId === changedValues.deliveryAddress)[0]
          form.setFieldsValue({
            ...newValues,
            postalCode: addr.postalCode,
            deliveryName: addr.deliveryName,
            warehouseNote: addr.notes,
            deliveryMethodId: addr.deliveryMethodId
          })
          return
        }
        break
      case 'deliveryName':
        if (changedValues['deliveryName']) {
          const addr: any = addressOptions.filter((row: any) => row.addressId === changedValues.deliveryName)[0]
          form.setFieldsValue({
            ...newValues,
            deliveryAddress: addr.addressId,
            postalCode: addr.postalCode,
            deliveryName: addr.deliveryName,
            warehouseNote: addr.notes,
            deliveryMethodId: addr.deliveryMethodId
          })
          return
        }
        break
      case 'orderProduct':
        orderProductChangedValueIndex = changedValues['orderProduct'].length - 1
        setOrderProductChangedValueIndex(orderProductChangedValueIndex)
        orderProductChangedValue = changedValues['orderProduct'][orderProductChangedValueIndex]
        if (orderProductChangedValue && orderProductChangedValue.productId && (Object.keys(orderProductChangedValue).length === 1)) {
          setProductInfosToForm(orderProductChangedValueIndex, orderProductChangedValue?.productId, newValues)
          return
        }
        break
      case 'orderOption':
        orderOptionChangedValueIndex = changedValues['orderOption'].length - 1
        setOrderOptionChangedValueIndex(orderOptionChangedValueIndex)
        orderOptionChangedValue = changedValues['orderOption'][orderOptionChangedValueIndex]
        if (orderOptionChangedValue && orderOptionChangedValue.optionId && (Object.keys(orderOptionChangedValue).length === 1)) {
          setProductOptionInfosToForm(orderOptionChangedValueIndex, orderOptionChangedValue?.optionId, newValues)
          return
        }
        break
    }
  }

  // REQ Date = Order Date + Working Days（跳过周末和节假日）
  const getLeadtimeBaseDate = () => (orderData?.orderDate ? moment.utc(orderData.orderDate).toDate() : new Date())

  const orderProductChange = (form: any) => {
    const formNewestValues = form.getFieldsValue()
    form.setFieldsValue({
      ...formNewestValues,
      orderProduct: autoPrice(formNewestValues, 'orderProduct'),
      orderOption: autoPrice2(formNewestValues, 'orderOption', orderOptionChangedValueIndex)
    })
  }

  const autoPrice = (formNewestValues: any, key: any) => {
    const orderKey: any = formNewestValues[key] || []
    const len = formNewestValues?.orderProduct?.length || 0
    for (let idx = 0; idx < len; idx++) {
      const orderKeyChangedValue = orderKey[idx]
      let changedFieldObj = {}
      if (orderKeyChangedValue?.unitPrice && orderKeyChangedValue?.quantity) {
        changedFieldObj = { price: orderKeyChangedValue.unitPrice * orderKeyChangedValue.quantity }
      }
      orderKey[idx] = { ...orderKeyChangedValue, ...changedFieldObj }
    }
    return orderKey
  }

  const autoPrice2 = (formNewestValues: any, key: any, index: any) => {
    const orderKey: any = formNewestValues[key] || []
    if (index === undefined || !orderKey[index]) return orderKey
    orderKey[index] = { ...orderKey[index] }
    return orderKey
  }

  const setProductOptionInfosToForm = async (index: any, optionId: any, newValues: any) => {
    const result = await ApiRequest({
      urlInfoKey: urlKey.ProductOption,
      type: urlType.GetById,
      dataId: optionId,
      isShowSpinner: false
    })
    if (result && newValues) {
      const productOptionInfos = result.data.data
      const orderOption: any = newValues.orderOption || []
      orderOption[index] = {
        ...orderOption[index],
        unitPrice: productOptionInfos.price || 0,
        price: orderOption[index].price,
        quantity: 1
      }
      form.setFieldsValue({ ...newValues, orderOption })
    }
  }

  const setProductInfosToForm = async (index: any, productId: any, newValues: any) => {
    // 使用保存的 group1Id 或从表单中获取
    const currentGroup1Id = group1Id || form.getFieldsValue()?.group1Id || null
    const query = `Product/GetProductById?id=${productId}${currentGroup1Id !== null && currentGroup1Id !== '' ? `&group1Id=${currentGroup1Id}` : ''}`

    const result = await ApiRequest({
      url: query,
      method: 'get',
      isShowSpinner: false
    })

    if (result == undefined) {
      await SweetAlertService.errorMessage('Get product error')
      return
    }
    if (result && newValues) {
      const productInfos = result.data.data
      const minPrice = productInfos.minPrice || 0 // 从产品信息中获取最低价格
      const quotationResult = await ApiRequest({
        url: 'Quotation/GetQuotationByCustomerId?id=' + (productInfos.customerId || form.getFieldsValue()?.customerId) + '&draft=0',
        method: 'get',
        isShowSpinner: false
      })
      let unitPriceFromQuotation = 0, quotationIdFromQuotation = null
      if (quotationResult == undefined) {
        await SweetAlertService.errorMessage('Get Quotation error')
        return
      }
      if (quotationResult?.data?.data.length) {
        const quotationItem: any = []
        for (const quote of quotationResult.data.data) {
          quotationItem.push(...quote.quotationItem)
        }
        if (quotationItem?.length) {
          const productQuotationItem = quotationItem.filter((row: any) => row.productId === productId)
          if (productQuotationItem?.length) {
            unitPriceFromQuotation = productQuotationItem[0].price
            quotationIdFromQuotation = productQuotationItem[0].quotationId
          } else {
            await SweetAlertService.errorMessage('This product is not included in the Quotation.')
          }
        } else {
          await SweetAlertService.errorMessage('There is not any product item in the Quotation.')
        }
      } else {
        await SweetAlertService.errorMessage('There is not an active Quotation for this customer.')
        return
      }
      // 价格检查：如果报价价格低于最低价格，显示警告
      if (unitPriceFromQuotation < minPrice && minPrice > 0) {
        const warningMessage = `Warning: Quotation price ($${unitPriceFromQuotation.toFixed(2)}) is lower than minimum price ($${minPrice.toFixed(2)}) for this product.`
        await SweetAlertService.errorMessage(warningMessage)
        setPriceWarnings(prev => ({ ...prev, [index]: true }))
      } else {
        setPriceWarnings(prev => ({ ...prev, [index]: false }))
      }
      const orderProduct: any = newValues.orderProduct || []
      orderProduct[index] = {
        productId: productInfos.productId,
        quantity: productInfos.minOrderQuantity || 0,
        unitPrice: unitPriceFromQuotation || 0,
        price: (unitPriceFromQuotation || 0) * (productInfos.minOrderQuantity || 0),
        marginOfError: productInfos.marginOfError || 0,
        packagingQty: productInfos.baseProduct.packagingType.quantity || 0,
        quotationId: quotationIdFromQuotation,
        minPrice: minPrice // 保存最低价格用于后续检查
      }
      form.setFieldsValue({ ...newValues, orderProduct })
      setSubtotal(getSubtotal(form))
    }
  }

  const setCustomerInfosToForm = async (customerId: any, newValues: any) => {
    setProducts(customerId)
    setAddress(customerId)
    const result = await ApiRequest({
      urlInfoKey: urlKey.Customer,
      type: urlType.GetById,
      dataId: customerId,
      isShowSpinner: false
    })

    if (result) {
      const customerInfos = result.data.data
      if (customerInfos.statusId != 1) {
        SweetAlertService.errorMessage('Sorry, this customer is currently inactive. Please activate their account first.')
        return
      }
      // 保存 group1Id
      if (customerInfos.group1Id) {
        setGroup1Id(customerInfos.group1Id)
      }
      let DeliveryFeeFlag
      const orderOptionForcust: IOrderOption[] = []
      if (customerInfos.deliveryFeeFlag) {
        const resDeliveryFeeFlags = await ApiRequest({
          url: 'DeliveryMethod/GetAllDeliveryFeeFlags',
          method: 'get'
        })
        if (resDeliveryFeeFlags.data && resDeliveryFeeFlags.data.data) {
          const DeliveryFeeFlags = resDeliveryFeeFlags.data.data.find((e: any) => e.flagId == customerInfos.deliveryFeeFlag)
          DeliveryFeeFlag = DeliveryFeeFlags.flagName
          if (customerInfos.deliveryFeeFlag == 2)
            orderOptionForcust.push({ optionId: 1, quantity: 1, unitPrice: 1, price: null })
        }
      }
      form.setFieldsValue({
        ...newValues,
        orderProduct: [],
        orderOption: orderOptionForcust,
        customerId: customerInfos.customerId,
        employeeId: customerInfos.employeeId,
        deliveryCityId: customerInfos.cityId,
        newWarehouseNote: customerInfos.noteToWarehouse,
        comments: customerInfos.noteToProduction,
        deliveryFee: DeliveryFeeFlag,
        paymentCycleId: customerInfos.paymentCycleId
      })
      setSubtotal(getSubtotal(form))
    }
  }

  const onFormBlur = () => {
    orderProductChange(form)
    setSubtotal(getSubtotal(form))
  }

  const getSubtotal = (form: any) => {
    // 取整个表单 store（true），字段未注册时也能拿到值
    const formValue = form.getFieldsValue(true)
    return (
      (formValue.orderProduct || []).reduce((a: number, c: IOrderProduct) => a + (c?.price || 0), 0) +
      (formValue.orderOption || []).reduce((a: number, c: IOrderOption) => a + (c?.price || 0), 0)
    )
  }

  const checkIfOrderProductChanged = () => {
    const orderProductValues: IOrderProduct[] = form.getFieldsValue().orderProduct
    const orderProductOriginValues: IOrderProduct[] = orderData?.orderProduct
    if ((!orderProductOriginValues || !orderProductOriginValues?.length) &&
      (!orderProductValues || !orderProductValues.length)) {
      return false
    }
    if (orderProductOriginValues?.length != orderProductValues?.length) {
      return true
    }
    for (const row of orderProductValues) {
      const oldRow: IOrderProduct = orderProductOriginValues.filter((item: IOrderProduct) => row?.orderProductId === item?.orderProductId)[0]
      if (!oldRow || !(
        oldRow.productId === row.productId &&
        Math.abs(oldRow.price - row.price) <= 0.1 &&
        oldRow.quantity === row.quantity
      )) {
        return true
      }
    }
    return false
  }

  const saveOrder = async () => {
    // Prevent duplicate submissions
    if (isSubmitting) return

    try {
      setIsSubmitting(true)

      try {
        await form.validateFields()
      } catch (e) {
        SweetAlertService.errorMessage('Unable to save. Please verify input contents for required fields including required date, order date, customer, delivery address, and ensure that they have been filled out.')
        return
      }
      orderProductChange(form)
      const subtotalCurrent = getSubtotal(form)
      setSubtotal(subtotalCurrent)
      const formValues: any = await form.validateFields()
      formValues.leadtimeFlag = formValues.leadtimeFlag ?? true
      const operEmployeeId = getCookie('customerUserId') ? 0 : parseInt(getCookie('id'))
      if (operEmployeeId == null) {
        SweetAlertService.errorMessage('Please check your login status.')
        return
      }

      const requestValues = {
        ...formValues,
        orderId: orderData.orderId,
        paid: formValues.paid ? 1 : 0,
        deliveryAsap: formValues.deliveryAsap ? 1 : 0,
        leadtimeFlag: leadtimeFlag ? 1 : 0,
        deliveryAddress: typeof (formValues.deliveryAddress) === 'number'
          ? addressOptions.filter((row: any) => row.addressId === formValues.deliveryAddress)[0]['address']
          : (formValues.deliveryAddress || ''),
        // Convert Switch boolean values to string values
        warehouseNoteType: formValues.warehouseNoteType ? 'Special' : 'Regular',
        productionNoteType: formValues.productionNoteType ? 'Special' : 'Regular',
        accountNoteType: formValues.accountNoteType ? 'Special' : 'Regular',
        orderProduct: formValues.orderProduct?.map((row: IOrderProduct) => ({
          ...row,
          orderId: orderData.orderId
        })),
        priceInclgst: Math.round(subtotalCurrent * 0.15 * 100) / 100,
        totalPrice: Math.round((subtotalCurrent + (Math.round(subtotalCurrent * 0.15 * 100) / 100)) * 100) / 100,
        orderOption: formValues.orderOption,
        operEmployeeId: operEmployeeId,
        createdEmployeeId: orderData?.createdEmployeeId ?? operEmployeeId,
        // UpdateOnlineOrder 会用请求里的值覆盖父单号，原样传回避免被清空
        parentOrderNo: orderData?.parentOrderNo
      }
      // 产品有变化：UpdateOnlineOrder 原地更新（UpdateFullOrder 会重建订单并清空 OrderSourceId，导致无法审批）
      // 产品没变：UpdateOrder，原地更新且能保存全部字段
      // TODO: 后端给 UpdateOnlineOrderDto 补上 NewWarehouseNote / ProductionNoteType / Leadtime / LeadtimeFlag 后可统一用 UpdateOnlineOrder
      const isProductChanged: boolean = checkIfOrderProductChanged()
      const result = await ApiRequest({
        url: isProductChanged ? 'OrderFromCustomer/UpdateOnlineOrder' : 'SalesOrder/UpdateOrder',
        method: 'put',
        data: requestValues
      })
      if (result) {
        await SweetAlertService.successMessage('Submit successfully')
        onSaved()
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!order || !orderData) return null

  const gst = subtotal * 0.15
  const formChangeLocked = orderData.orderStatus?.orderStatusId >= 15 && checkIfOrderProductChanged()

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div>
        <Text type="secondary">Subtotal</Text> <b>${subtotal.toFixed(2)}</b>
        <Text type="secondary" style={{ marginLeft: 12 }}>GST</Text> <b>${gst.toFixed(2)}</b>
        <Text type="secondary" style={{ marginLeft: 12 }}>Total</Text>&nbsp;
        <Text strong style={{ fontSize: 18, color: '#065f46' }}>${(subtotal + gst).toFixed(2)}</Text>
      </div>
      <Space>
        {/* 有未保存修改时禁用，避免基于未保存的数据分单 / 审批 */}
        <Tooltip title={isDirty ? 'Save changes before splitting' : ''}>
          <Button icon={<SplitCellsOutlined />} disabled={isDirty || isSubmitting || !order.orderProduct.length}
                  onClick={() => setSplitVisible(true)}>
            Split Order
          </Button>
        </Tooltip>
        <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
        <Button disabled>Save as Draft</Button>
        <Button icon={<SaveOutlined />} disabled={formChangeLocked || isSubmitting} loading={isSubmitting}
                onClick={saveOrder}>
          Confirm
        </Button>
        <Tooltip title={isDirty ? 'Save changes before approving' : ''}>
          <span>
            <Popconfirm title="Approve this order?" onConfirm={() => onApprove(order)} disabled={isDirty || isSubmitting}>
              <Button type="primary" icon={<CheckOutlined />} disabled={isDirty || isSubmitting}>Approve</Button>
            </Popconfirm>
          </span>
        </Tooltip>
      </Space>
    </div>
  )

  return (
    <Drawer
      title={
        <Space>
          <span>{order.orderNo}</span>
          {order.deliveryAsap && <Tag color="red" icon={<AlertOutlined />}>Urgent</Tag>}
        </Space>
      }
      width={1200}
      visible={visible}
      onClose={onClose}
      footer={footer}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        scrollToFirstError
        validateMessages={validateMessages}
        onValuesChange={onFormChange}
        onBlur={onFormBlur}
        className="order-approved-edit-form"
      >
        <Divider orientation="left" style={{ marginTop: 0 }}>Customer</Divider>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="customerId" label="Customer" rules={[{ required: true }]}>
              {commonFormSelect(urlKey.Customer, customerOptions, ['company', 'customerCode'])}
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="employeeId" label="Sales">
              {commonFormSelect(urlKey.Employee, employeeOptions, ['firstName', 'lastName'], true)}
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="custOrderNo" label="PO">
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={4}>
            <Form.Item name="orderNo" label="Order No">
              <Input disabled />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="orderDate" label="Order Date" rules={[{ required: true }]}>
              <DatePicker disabled format="YYYY-MM-DD" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="orderStatusId" label="Order Status">
              {commonFormSelect(urlKey.OrderStatus, orderStatusOptions, [], true)}
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="paymentCycleId" label="Payment Term">
              {commonFormSelect(urlKey.PaymentCycle, paymentCycleOptions, ['paymentCycleName'], true)}
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="invoiceNumber" label="Invoice Number">
              <Input disabled />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Delivery</Divider>
        <Row gutter={16}>
          <Col span={3}>
            <Form.Item name="leadtimeFlag" label="Leadtime/REQ" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item name="requiredDate" label="REQ Date" rules={[{ required: true }]}>
              <DatePicker disabled={leadtimeFlag} format="YYYY-MM-DD" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item name="leadtime" label="Working Days" rules={[{ required: leadtimeFlag }]} hidden={!leadtimeFlag}>
              <Select options={[2, 10, 15, 20].map(n => ({ value: n, label: `${n} Working days` }))} />
            </Form.Item>
          </Col>
          <Col span={3}>
            <Form.Item name="deliveryAsap" label="Urgent" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="urgentNote" label="Urgent Reason">
              <Input.TextArea disabled showCount maxLength={150} autoSize />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item name="deliveryMethodId" label="Delivery Method">
              {commonFormSelect(urlKey.DeliveryMethod, deliveryMethodOptions, [], !!getCookie('customerUserId'))}
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="deliveryName" label="Delivery Name">
              {commonFormSelect(urlKey.ExtraAddress, addressOptions, ['deliveryName'])}
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="deliveryAddress" label="Delivery Address" rules={[{ required: true }]}>
              {commonFormSelect(urlKey.ExtraAddress, addressOptions, ['address'])}
            </Form.Item>
          </Col>
        </Row>
        {/* 配送地址的联系人信息，只读，不随表单提交 */}
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item label="Contact Name">
              <Input disabled value={selectedAddress?.contactName} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Phone">
              <Input disabled value={selectedAddress?.phone} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Email">
              <Input disabled value={selectedAddress?.email} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={16}>
            <Form.Item name="warehouseNote" label="Delivery Instruction">
              <Input.TextArea showCount maxLength={500} autoSize />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="deliveryFee" label="Price includes shipping fee OR Extra charges for shipping">
              <Input readOnly />
            </Form.Item>
          </Col>
        </Row>
        {/* 不显示但要随表单提交的字段（与原弹窗一致） */}
        <Form.Item name="paid" valuePropName="checked" hidden><Switch /></Form.Item>
        <Form.Item name="deliveryDate" hidden><DatePicker /></Form.Item>
        <Form.Item name="deliveryCityId" hidden>{commonFormSelect(urlKey.City, deliveryCityOptions)}</Form.Item>

        <Divider orientation="left">Items</Divider>
        <Row gutter={8}>
          <Col span={8}>{fieldLabel('Product', true)}</Col>
          <Col span={3}>{fieldLabel('$Per Carton', true)}</Col>
          <Col span={3}>{fieldLabel('QTY Per Carton', true)}</Col>
          <Col span={3}>{fieldLabel('QTY', true)}</Col>
          <Col span={3}>{fieldLabel('$Price', true)}</Col>
          <Col span={3}>{fieldLabel('Margin of Error')}</Col>
        </Row>
        <Form.List name="orderProduct">
          {(fields, { add, remove }) => (
            <>
              {fields.map(field => (
                <Row gutter={8} key={field.key} align="top">
                  <Col span={8}>
                    <Form.Item name={[field.name, 'productId']} rules={[{ required: true }]}>
                      {commonFormSelect(urlKey.Product, productsOptions, ['productCode', 'productName'], false, null, null, '', null, false, true)}
                    </Form.Item>
                  </Col>
                  <Col span={3}>
                    <Form.Item name={[field.name, 'unitPrice']} rules={[{ required: true, type: 'number', min: 0 }]}>
                      <InputNumber disabled style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={3}>
                    <Form.Item name={[field.name, 'packagingQty']} rules={[{ required: true, type: 'number', min: 0 }]}>
                      <InputNumber step="0.01" disabled style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={3}>
                    <Form.Item name={[field.name, 'quantity']} rules={[{ required: true, type: 'number', min: 0 }]}>
                      <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={3}>
                    <Form.Item name={[field.name, 'price']} rules={[{ required: true, type: 'number', min: 0 }]}>
                      <InputNumber step="0.01" disabled style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={3}>
                    <Form.Item name={[field.name, 'marginOfError']} rules={[{ type: 'number', min: 0 }]}>
                      <InputNumber disabled style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Form.Item name={[field.name, 'quotationId']} hidden><Input /></Form.Item>
                  <Col span={1}>
                    <Tooltip title="Delete">
                      <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                    </Tooltip>
                  </Col>
                </Row>
              ))}
              <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add()}>Add Product</Button>
            </>
          )}
        </Form.List>

        <Divider orientation="left">Product Options</Divider>
        <Row gutter={8}>
          <Col span={8}>{fieldLabel('Product Option', true)}</Col>
          <Col span={3}>{fieldLabel('Unit Price')}</Col>
          <Col span={3}>{fieldLabel('QTY', true)}</Col>
          <Col span={3}>{fieldLabel('Price', true)}</Col>
        </Row>
        <Form.List name="orderOption">
          {(fields, { add, remove }) => (
            <>
              {fields.map(field => (
                <Row gutter={8} key={field.key} align="top">
                  <Col span={8}>
                    <Form.Item name={[field.name, 'optionId']} rules={[{ required: true }]}>
                      {commonFormSelect(urlKey.ProductOption, productOptionOptions)}
                    </Form.Item>
                  </Col>
                  <Col span={3}>
                    <Form.Item name={[field.name, 'unitPrice']} rules={[{ type: 'number', min: 0 }]}>
                      <InputNumber step="1" disabled style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={3}>
                    <Form.Item name={[field.name, 'quantity']} rules={[{ required: true, type: 'number', min: 0 }]}>
                      <InputNumber disabled style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={3}>
                    <Form.Item name={[field.name, 'price']} rules={[{ required: true, type: 'number', min: 0 }]}>
                      <InputNumber step="1" style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={1}>
                    <Tooltip title="Delete">
                      <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                    </Tooltip>
                  </Col>
                </Row>
              ))}
              <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({ quantity: 1 })}>Add Option</Button>
            </>
          )}
        </Form.List>

        <Divider orientation="left">Notes</Divider>
        <Row gutter={16}>
          {[
            { name: 'newWarehouseNote', type: 'warehouseNoteType', label: 'Note to Warehouse' },
            { name: 'comments', type: 'productionNoteType', label: 'Note to Production' },
            { name: 'accountNote', type: 'accountNoteType', label: 'Note to Account' }
          ].map(note => (
            <Col span={8} key={note.name}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span>{note.label}</span>
                <Form.Item name={note.type} valuePropName="checked" noStyle>
                  <Switch size="small" checkedChildren="Sp" unCheckedChildren="Reg" />
                </Form.Item>
              </div>
              <Form.Item name={note.name}>
                <Input.TextArea showCount maxLength={300} autoSize={{ minRows: 2, maxRows: 4 }} />
              </Form.Item>
            </Col>
          ))}
        </Row>
      </Form>

      <div style={{ color: '#8c8c8c', fontSize: 12 }}>
        Created by {order.createdBy} · Order date {moment(order.orderDate).format('DD/MM/YYYY')}
      </div>

      <OrderApprovedSplitModal
        order={order}
        visible={splitVisible}
        onCancel={() => setSplitVisible(false)}
        onConfirm={(options) => { onSplit(order, options); setSplitVisible(false) }}
      />
    </Drawer>
  )
}

export default OrderApprovedEditDrawer
